import React, { useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  StructureRoofDetail,
  StructureRoofFace,
  sortStructures,
  isValidStructureNumericValue,
} from '../../../utils/aurora-mapper.utils';
import { ConfirmationModal, ConfirmationType } from './ConfirmationModal';
import { MAX_STRUCTURES } from '../../../constants';

interface AuroraStructuresModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  onBackToReview?: () => void;
  onConfirm: (details: StructureRoofDetail[]) => void;
  onChange?: (details: StructureRoofDetail[]) => void;
  initialDetails: StructureRoofDetail[];
  embedded?: boolean;
}

const FACE_FIELDS: { key: keyof StructureRoofFace; label: string }[] = [
  { key: 'area', label: 'Area' },
  { key: 'modules_area', label: 'Modules Area' },
  { key: 'number_of_modules', label: '# Modules' },
  { key: 'azimuth', label: 'Azimuth' },
  { key: 'pitch', label: 'Pitch' },
];

const AREA_KEYS = new Set<keyof StructureRoofFace>(['area', 'modules_area']);

export const AuroraStructuresModal: React.FC<AuroraStructuresModalProps> = ({
  isOpen,
  onClose,
  onBack,
  onBackToReview,
  onConfirm,
  onChange,
  initialDetails,
  embedded = false,
}) => {
  const [structures, setStructures] = useState<StructureRoofDetail[]>(() =>
    sortStructures(initialDetails.map(s => ({
      ...s,
      faces: s.faces.map(f => ({ ...f })),
    }))),
  );

  // Rename state: which structure index is being edited
  const [editingNameIdx, setEditingNameIdx] = useState<number | null>(null);
  const [editingNameValue, setEditingNameValue] = useState('');

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<{ idx: number; name: string } | null>(null);

  // Merge state: which structure is showing the merge dropdown
  const [mergeSourceIdx, setMergeSourceIdx] = useState<number | null>(null);

  // Track which numeric input is focused (to show raw value while editing, formatted otherwise)
  const [editingKey, setEditingKey] = useState<string | null>(null);

  // Close merge dropdown on outside click
  React.useEffect(() => {
    if (mergeSourceIdx === null) return;
    const handleClick = () => setMergeSourceIdx(null);
    const timer = setTimeout(() => document.addEventListener('click', handleClick), 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClick);
    };
  }, [mergeSourceIdx]);

  // Sync changes to parent via onChange
  React.useEffect(() => {
    if (isOpen) onChange?.(structures);
  }, [structures, isOpen, onChange]);

  React.useEffect(() => {
    if (isOpen) {
      const sorted = sortStructures(initialDetails.map(s => ({
        ...s,
        faces: s.faces.map(f => ({ ...f })),
      })));
      setStructures(sorted);
      setEditingNameIdx(null);
      setDeleteTarget(null);
      setMergeSourceIdx(null);
    }
  }, [initialDetails, isOpen]);

  const updateFaceField = useCallback(
    (structIdx: number, faceIdx: number, field: keyof StructureRoofFace, value: number) => {
      setStructures(prev =>
        prev.map((s, si) =>
          si === structIdx
            ? { ...s, faces: s.faces.map((f, fi) => fi === faceIdx ? { ...f, [field]: value } : f) }
            : s,
        ),
      );
    },
    [],
  );

  // Round face area to 2 decimals and recalculate structure totals (called on blur)
  const commitFaceArea = useCallback(
    (structIdx: number) => {
      setStructures(prev =>
        prev.map((s, si) => {
          if (si !== structIdx) return s;
          const faces = s.faces.map(f => ({
            ...f,
            area: parseFloat(f.area.toFixed(2)),
            modules_area: parseFloat(f.modules_area.toFixed(2)),
          }));
          return {
            ...s,
            faces,
            total_area: parseFloat((faces.reduce((sum, f) => sum + f.area, 0) + s.non_module_faces_area).toFixed(2)),
            modules_area: parseFloat(faces.reduce((sum, f) => sum + f.modules_area, 0).toFixed(2)),
          };
        }),
      );
    },
    [],
  );

  // --- Rename handlers ---
  const startRename = useCallback((idx: number, currentName: string) => {
    setEditingNameIdx(idx);
    setEditingNameValue(currentName);
  }, []);

  const confirmRename = useCallback(() => {
    if (editingNameIdx === null) return;
    const trimmed = editingNameValue.trim();
    if (trimmed) {
      setStructures(prev =>
        prev.map((s, i) => (i === editingNameIdx ? { ...s, structure_name: trimmed } : s)),
      );
    }
    setEditingNameIdx(null);
    setEditingNameValue('');
  }, [editingNameIdx, editingNameValue]);

  const cancelRename = useCallback(() => {
    setEditingNameIdx(null);
    setEditingNameValue('');
  }, []);

  // --- Delete handlers ---
  const handleDeleteStructure = useCallback(() => {
    if (deleteTarget === null) return;
    setStructures(prev => sortStructures(prev.filter((_, i) => i !== deleteTarget.idx)));
    setDeleteTarget(null);
  }, [deleteTarget]);

  // --- Merge handler ---
  const handleMerge = useCallback((sourceIdx: number, targetIdx: number) => {
    setStructures(prev => {
      const source = prev[sourceIdx];
      const target = prev[targetIdx];
      const merged: StructureRoofDetail = {
        ...target,
        total_area: (target.total_area || 0) + (source.total_area || 0),
        modules_area: (target.modules_area || 0) + (source.modules_area || 0),
        faces: [...target.faces.map(f => ({ ...f })), ...source.faces.map(f => ({ ...f }))],
        non_module_faces_count: (target.non_module_faces_count || 0) + (source.non_module_faces_count || 0),
        non_module_faces_area: (target.non_module_faces_area || 0) + (source.non_module_faces_area || 0),
      };
      return sortStructures(
        prev
          .map((s, i) => (i === targetIdx ? merged : s))
          .filter((_, i) => i !== sourceIdx),
      );
    });
    setMergeSourceIdx(null);
  }, []);

  const isValid = useMemo(() => {
    return structures.every(
      s =>
        isValidStructureNumericValue(s.total_area) &&
        isValidStructureNumericValue(s.modules_area) &&
        s.faces.every(
          f =>
            isValidStructureNumericValue(f.area) &&
            isValidStructureNumericValue(f.modules_area) &&
            isValidStructureNumericValue(f.number_of_modules) &&
            isValidStructureNumericValue(f.azimuth) &&
            isValidStructureNumericValue(f.pitch),
        ),
    );
  }, [structures]);

  const canConfirm = isValid && structures.length <= MAX_STRUCTURES;

  const handleConfirm = useCallback(() => {
    if (canConfirm) onConfirm(structures);
  }, [structures, canConfirm, onConfirm]);

  if (!isOpen) return null;

  const isEmpty = structures.length === 0;
  const exceedsMax = structures.length > MAX_STRUCTURES;

  const content = (
    <>
      {/* Header */}
        <div className="px-5 pt-3 pb-3 3xl:px-6 3xl:pt-4 3xl:pb-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 3xl:w-10 3xl:h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-sm">
                <svg className="w-5 h-5 3xl:w-6 3xl:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm 3xl:text-lg font-semibold text-slate-800">Structures with Roofs</h3>
                <p className="text-[11px] 3xl:text-sm text-slate-400">Review and edit roof structure details from Aurora design</p>
              </div>
            </div>
            {!isEmpty && (
              <div className="text-center">
                <div className="text-[10px] 3xl:text-sm uppercase tracking-wider font-semibold text-slate-400">Structures</div>
                <div className={`text-sm 3xl:text-lg font-mono font-semibold ${exceedsMax ? 'text-red-600' : 'text-blue-600'}`}>
                  {structures.length}
                  {exceedsMax && <span className="text-[10px] 3xl:text-sm text-red-500 font-sans ml-1">/ {MAX_STRUCTURES} max</span>}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 px-5 py-4 3xl:px-6 3xl:py-5 overflow-y-auto">
          {isEmpty ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-12 h-12 3xl:w-14 3xl:h-14 rounded-xl bg-slate-100 flex items-center justify-center mb-3">
                <svg className="w-6 h-6 3xl:w-7 3xl:h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </div>
              <p className="text-sm 3xl:text-lg font-medium text-slate-600">No roof structures found</p>
              <p className="text-[11px] 3xl:text-sm text-slate-400 mt-1">The Aurora design does not contain roof structure data.</p>
            </div>
          ) : (
            <div className="space-y-5 3xl:space-y-6">
              {exceedsMax && (
                <div className="flex items-center gap-2.5 px-4 py-2.5 3xl:px-5 3xl:py-3 rounded-lg bg-red-50 border border-red-200">
                  <svg className="w-4 h-4 3xl:w-5 3xl:h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-xs 3xl:text-sm font-medium text-red-700">
                    Too many structures ({structures.length}). Maximum allowed is {MAX_STRUCTURES}. Please merge or delete structures to continue.
                  </p>
                </div>
              )}
              {structures.map((structure, sIdx) => (
                <div key={sIdx} className="rounded-xl border border-slate-200 border-l-[3px] border-l-blue-500 overflow-hidden">
                  {/* Structure header — name + area summaries */}
                  <div className="px-4 py-2.5 3xl:px-5 3xl:py-3 bg-slate-50 border-b border-slate-200">
                    <div className="flex items-center gap-3 min-w-0 flex-wrap">
                      {editingNameIdx === sIdx ? (
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <input
                            type="text"
                            value={editingNameValue}
                            onChange={e => setEditingNameValue(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') confirmRename();
                              if (e.key === 'Escape') cancelRename();
                            }}
                            autoFocus
                            className="px-2 py-1 3xl:px-2.5 3xl:py-1.5 text-xs 3xl:text-base font-bold uppercase tracking-wider text-slate-700 border border-blue-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white w-48 3xl:w-64"
                          />
                          <button
                            onClick={confirmRename}
                            className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                            title="Save"
                          >
                            <svg className="w-3.5 h-3.5 3xl:w-4 3xl:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <button
                            onClick={cancelRename}
                            className="p-1 text-slate-400 hover:bg-slate-100 rounded transition-colors"
                            title="Cancel"
                          >
                            <svg className="w-3.5 h-3.5 3xl:w-4 3xl:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <h4
                          className="text-xs 3xl:text-base font-bold uppercase tracking-wider text-slate-700 cursor-pointer hover:text-blue-600 transition-colors flex-shrink-0"
                          onClick={() => startRename(sIdx, structure.structure_name)}
                          title="Click to rename"
                        >
                          {structure.structure_name}
                        </h4>
                      )}

                      {editingNameIdx !== sIdx && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => startRename(sIdx, structure.structure_name)}
                            className="p-1 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="Rename structure"
                          >
                            <svg className="w-3.5 h-3.5 3xl:w-4 3xl:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          {structures.length > 1 && (
                            <div className="relative">
                              <button
                                onClick={() => setMergeSourceIdx(mergeSourceIdx === sIdx ? null : sIdx)}
                                className={`p-1 rounded transition-colors ${
                                  mergeSourceIdx === sIdx
                                    ? 'text-amber-600 bg-amber-50'
                                    : 'text-amber-400 hover:text-amber-600 hover:bg-amber-50'
                                }`}
                                title="Merge into another structure"
                              >
                                <svg className="w-3.5 h-3.5 3xl:w-4 3xl:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                </svg>
                              </button>
                              {mergeSourceIdx === sIdx && (
                                <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-10 min-w-[160px]">
                                  <div className="px-2.5 py-1.5 text-[9px] 3xl:text-xs font-semibold text-slate-400 uppercase tracking-wider">
                                    Merge into...
                                  </div>
                                  {structures.map((target, tIdx) => {
                                    if (tIdx === sIdx) return null;
                                    return (
                                      <button
                                        key={tIdx}
                                        onClick={() => handleMerge(sIdx, tIdx)}
                                        className="w-full text-left px-2.5 py-1.5 text-[11px] 3xl:text-sm font-medium text-slate-700 hover:bg-amber-50 hover:text-amber-700 transition-colors"
                                      >
                                        {target.structure_name}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                          <button
                            onClick={() => setDeleteTarget({ idx: sIdx, name: structure.structure_name })}
                            className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Delete structure"
                          >
                            <svg className="w-3.5 h-3.5 3xl:w-4 3xl:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      )}

                      {/* Area summary badges (read-only) */}
                      <div className="flex items-center gap-2 ml-auto flex-shrink-0">
                        <div className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-100/80 border border-slate-200/60 rounded-md">
                          <span className="text-[9px] 3xl:text-xs font-semibold uppercase tracking-wider text-slate-500">Total</span>
                          <span className="text-[11px] 3xl:text-sm font-mono font-semibold text-slate-700">{structure.total_area.toFixed(2)}</span>
                          <span className="text-[8px] 3xl:text-[10px] text-slate-400">sqft</span>
                        </div>
                        <div className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-100/80 border border-slate-200/60 rounded-md">
                          <span className="text-[9px] 3xl:text-xs font-semibold uppercase tracking-wider text-slate-500">Modules</span>
                          <span className="text-[11px] 3xl:text-sm font-mono font-semibold text-slate-700">{structure.modules_area.toFixed(2)}</span>
                          <span className="text-[8px] 3xl:text-[10px] text-slate-400">sqft</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Faces table — compact */}
                  {structure.faces.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs 3xl:text-sm">
                        <thead>
                          <tr className="bg-slate-50/50">
                            <th className="px-2 py-1.5 3xl:px-3 3xl:py-2 text-left text-[9px] 3xl:text-xs font-semibold uppercase tracking-wider text-slate-400 w-8">#</th>
                            {FACE_FIELDS.map(ff => (
                              <th key={ff.key} className="px-2 py-1.5 3xl:px-3 3xl:py-2 text-left text-[9px] 3xl:text-xs font-semibold uppercase tracking-wider text-slate-400">
                                {ff.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {structure.faces.map((face, fIdx) => (
                            <tr key={fIdx} className="hover:bg-blue-50/30 transition-colors">
                              <td className="px-2 py-1 3xl:px-3 3xl:py-1.5 text-[9px] 3xl:text-xs font-mono text-slate-400">{fIdx + 1}</td>
                              {FACE_FIELDS.map(ff => {
                                const isArea = AREA_KEYS.has(ff.key);
                                const fKey = `f${sIdx}-${fIdx}-${ff.key}`;
                                const raw = face[ff.key];
                                return (
                                  <td key={ff.key} className="px-2 py-1 3xl:px-3 3xl:py-1.5">
                                    <input
                                      type="number"
                                      step={isArea ? '0.01' : 'any'}
                                      value={isArea ? (editingKey === fKey ? raw : raw.toFixed(2)) : raw}
                                      onFocus={isArea ? () => setEditingKey(fKey) : undefined}
                                      onBlur={isArea ? () => { setEditingKey(null); commitFaceArea(sIdx); } : undefined}
                                      onChange={e => {
                                        const str = e.target.value;
                                        if (isArea) {
                                          const dot = str.indexOf('.');
                                          if (dot !== -1 && str.length - dot - 1 > 2) return;
                                        }
                                        const v = parseFloat(str);
                                        if (isNaN(v)) return;
                                        updateFaceField(sIdx, fIdx, ff.key, v);
                                        if (isArea && Math.abs(Math.round((v - raw) * 100)) === 1) {
                                          commitFaceArea(sIdx);
                                        }
                                      }}
                                      className="w-full px-1.5 py-0.5 3xl:px-2 3xl:py-1 text-xs 3xl:text-sm font-mono border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="px-4 py-6 text-center bg-orange-50/50 border-t border-orange-100">
                      <p className="text-xs 3xl:text-sm font-medium text-orange-500">No faces with modules on this structure</p>
                    </div>
                  )}

                  {/* Non-module faces info */}
                  {structure.non_module_faces_count > 0 && (
                    <div className="mx-3 my-2 3xl:mx-4 3xl:my-2.5 px-2.5 py-1.5 3xl:px-3 3xl:py-2 bg-amber-50/50 rounded-md">
                      <p className="text-[9px] 3xl:text-xs text-amber-700">
                        <span className="font-semibold">{structure.non_module_faces_count}</span> roof {structure.non_module_faces_count === 1 ? 'face' : 'faces'} with no modules
                        {' '}· <span className="font-semibold">{structure.non_module_faces_area.toFixed(2)}</span> sq ft
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3.5 3xl:px-6 3xl:py-4 border-t border-slate-100 flex items-center justify-between flex-shrink-0">
          <p className="text-[10px] 3xl:text-sm text-slate-400">
            {isEmpty
              ? 'No structures to review'
              : 'Edit values as needed, then confirm to proceed'}
          </p>
          <div className="flex gap-2.5">
            <button
              onClick={onClose}
              className="px-3.5 py-1.5 3xl:px-4 3xl:py-2 text-xs 3xl:text-base font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 rounded-lg border border-slate-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!canConfirm && !isEmpty}
              className={`px-3.5 py-1.5 3xl:px-4 3xl:py-2 text-xs 3xl:text-base font-medium rounded-lg shadow-sm transition-all duration-150 ${
                canConfirm || isEmpty
                  ? 'text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-emerald-500/20'
                  : 'text-slate-400 bg-slate-100 cursor-not-allowed shadow-none'
              }`}
            >
              {isEmpty ? 'Continue' : 'Confirm Structures'}
            </button>
          </div>
        </div>

      {/* Delete confirmation */}
      <ConfirmationModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteStructure}
        title="Delete Structure"
        message={`Delete "${deleteTarget?.name}"? This structure and all its faces will be removed.`}
        type={ConfirmationType.DANGER}
        confirmButtonText="Delete"
        cancelButtonText="Keep"
      />
    </>
  );

  if (embedded) return content;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-5xl 3xl:max-w-7xl mx-auto shadow-2xl border border-gray-100 overflow-hidden flex flex-col h-[85vh]">
        {content}
      </div>
    </div>,
    document.body,
  );
};
